import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Settings2, TriangleAlert } from 'lucide-react';
import type { OutreachCampaign } from '@vagusx/clients';
import { campaignPlacesCalls } from '@vagusx/shared';
import { TablePage, SortableHeader } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAudiencePreview, useContactPolicy, useOutreachCampaigns } from '@/hooks/use-outreach';
import { CACHE_TIME_10_MINS } from '@/lib/query-client';

/**
 * How many patients this campaign's published audience matches right now.
 *
 * **One population-scale query per row with a published spec**, issued in
 * parallel — an organization with thirty campaigns opens thirty at once. The
 * number earns that: it is what tells an author whether the campaign they wrote
 * means what they thought, and without it the list cannot be judged at all.
 *
 * It is bounded two ways. A ten-minute `staleTime` rather than the editor's one
 * minute, because a list is read far more often than a campaign's audience
 * changes; and the cache key is the audience document, so campaigns sharing an
 * audience share one query. If campaign counts ever grow past a few dozen this
 * should move into the list response instead.
 */
export function MatchCount({ campaign }: { campaign: OutreachCampaign }): React.ReactElement {
  // Counted as the reconciler will count it: a campaign that places calls
  // requires a date of birth whatever its saved switch says (ADR-333).
  const { data, isLoading, isError } = useAudiencePreview(
    campaign.spec?.audience ?? null,
    campaign.spec ? campaignPlacesCalls(campaign.spec) : false,
    { staleTime: CACHE_TIME_10_MINS }
  );

  if (!campaign.spec) return <span className="text-muted-foreground">—</span>;
  if (isLoading) return <Skeleton className="h-4 w-8" />;
  if (isError) return <span className="text-muted-foreground">?</span>;
  if (!data) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="flex items-center gap-1.5">
      {/* Ready and not ready when there is something to check (a referral audience, or
          any campaign that calls); otherwise only "qualify". */}
      {data.ready !== undefined && data.notReady !== undefined ? (
        <span className="tabular-nums">
          {data.ready}
          <span className="text-muted-foreground"> ready · {data.notReady} not</span>
        </span>
      ) : (
        <span className="tabular-nums">{data.patients}</span>
      )}
      {data.aboveCeiling && (
        <Tooltip>
          <TooltipTrigger asChild>
            <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
          </TooltipTrigger>
          <TooltipContent>
            Too large to run — the reconciler refuses this campaign on every tick.
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * Of the runs that started in the last 30 days, how many met the goal because
 * of the campaign. Sent with the list, one grouped read for every campaign.
 */
function Last30Days({ campaign }: { campaign: OutreachCampaign }): React.ReactElement {
  const recent = campaign.last30Days;
  if (!recent) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="tabular-nums">
      {recent.succeeded}
      <span className="text-muted-foreground"> of {recent.joined}</span>
    </span>
  );
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  active: 'default',
  paused: 'secondary',
  draft: 'outline',
};

const columns: ColumnDef<OutreachCampaign>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <SortableHeader column={column}>Campaign</SortableHeader>,
    cell: ({ row }) => (
      <div className="min-w-0">
        <div className="font-medium">{row.original.name}</div>
        {/* The read-back answers "who does this call?" without opening anything. */}
        <div className="truncate text-xs text-muted-foreground">
          {row.original.audienceDescription ?? 'No version published yet'}
        </div>
      </div>
    ),
  },
  {
    id: 'ready',
    header: 'Ready to call',
    cell: ({ row }) => <MatchCount campaign={row.original} />,
  },
  {
    id: 'outcomes',
    header: 'Succeeded · 30 days',
    cell: ({ row }) => <Last30Days campaign={row.original} />,
  },
  {
    accessorKey: 'priority',
    header: ({ column }) => <SortableHeader column={column}>Priority</SortableHeader>,
    cell: ({ row }) => <span className="tabular-nums">{row.original.priority}</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <Badge variant={STATUS_VARIANT[row.original.status] ?? 'outline'} className="capitalize">
          {row.original.status}
        </Badge>
        {row.original.version != null && (
          <span className="text-xs text-muted-foreground">v{row.original.version}</span>
        )}
      </div>
    ),
  },
];

interface CampaignListProps {
  onNewCampaign: () => void;
  onOpenCampaign: (id: string) => void;
  onOpenPolicy: () => void;
  /** Hidden for a viewer without outreach:edit. */
  canEdit: boolean;
}

export function CampaignList({
  onNewCampaign,
  onOpenCampaign,
  onOpenPolicy,
  canEdit,
}: CampaignListProps): React.ReactElement {
  const { data, isLoading, error } = useOutreachCampaigns();
  const { data: policy } = useContactPolicy();

  return (
    <TablePage
      title="Campaigns"
      description="Who Iris reaches out to, when, and what she does when she gets through."
      columns={columns}
      data={data ?? []}
      total={data?.length}
      isLoading={isLoading}
      error={error}
      searchColumn="name"
      searchPlaceholder="Search campaigns..."
      onRowClick={(row) => onOpenCampaign(row.id)}
      emptyMessage="No campaigns yet."
      errorMessage="Failed to load campaigns."
      aboveTable={
        policy && (
          /**
           * The kill switch is org-wide and defaults off, so a campaign can
           * read as Active while nothing dials. Saying so here is the
           * difference between "it works" and an hour spent wondering why
           * nobody was called.
           */
          <div className="flex items-center justify-between rounded-md border px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <span
                className={
                  policy.enabled
                    ? 'h-2 w-2 rounded-full bg-primary'
                    : 'h-2 w-2 rounded-full bg-muted-foreground'
                }
              />
              {policy.enabled ? (
                <span>Automated calling is on.</span>
              ) : (
                <span className="text-muted-foreground">
                  Automated calling is off — no campaign dials anyone, whatever its status.
                </span>
              )}
              <span className="text-muted-foreground">
                Calling hours {policy.quietHoursStart}–{policy.quietHoursEnd} · at most{' '}
                {policy.maxContactsPerPatient} contacts per patient every{' '}
                {policy.maxContactsPerDays} days
              </span>
            </div>
            <Button variant="outline" size="sm" onClick={onOpenPolicy}>
              <Settings2 className="mr-2 h-4 w-4" />
              Contact rules
            </Button>
          </div>
        )
      }
      headerAction={
        canEdit && (
          <Button onClick={onNewCampaign}>
            <Plus className="mr-2 h-4 w-4" />
            New campaign
          </Button>
        )
      }
    />
  );
}
